import React from 'react';
import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://nivasapro.ai';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;
const SITE_NAME = 'NivasaPro.ai';

/**
 * SEOMeta — drop this into any page component to set per-page meta tags.
 *
 * Props:
 *   title       string  — page title (appended with " | NivasaPro.ai")
 *   description string  — meta description (≤160 chars ideal)
 *   canonical   string  — canonical path e.g. "/features"
 *   image       string  — absolute OG image URL (defaults to site OG image)
 *   noIndex     bool    — set true for auth/private pages
 *   schema      object  — JSON-LD structured data object (optional)
 */
const SEOMeta = ({
  title,
  description,
  canonical,
  image = DEFAULT_IMAGE,
  noIndex = false,
  schema,
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME}: AI-Powered Property Management`;
  const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : null;

  return (
    <Helmet>
      {/* Primary */}
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content="website" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Structured Data */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOMeta;
